import { useEffect, useState } from "react";

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/**
 * Whether to show the startup "Connecting to Server" busy dialog (C++
 * Ui_progress_info during the alarm interface's ORB connect + version check).
 *
 * Starts *open* on the desktop and closes once the backend confirms the
 * connection — either by the one-shot `get_server_connected` query or by the
 * `server-connected` event. Starting open matters: Tauri creates the config
 * windows before it runs the setup hook that kicks off the connect, but a local
 * ORB connect normally completes long before the JS bundle has mounted. A
 * mount-time query alone therefore almost always reports "already connected"
 * and the dialog never appears at all — the startup wait it exists to cover has
 * come and gone. A `server-connected` event is re-checked against
 * `get_server_connected` before it closes the dialog, so an emit that races a
 * gate the backend is about to open cannot lose the wait. The version-update
 * dialogs close it unconditionally, since they take over the screen (C++
 * behaviour).
 *
 * "Connected" on the backend means every startup gate: the ORB connect+register
 * handshake, the startup version check, and — in a tunnel session — the
 * TIGER_REMOTE_TUNNEL probe answered by the far side. The tunnel gate is what
 * keeps this dialog up during a real remote connect: the tunnel endpoint
 * accepts the local socket in milliseconds while the site link takes seconds
 * to minutes (ti_connect::server_startup_ready).
 *
 * Web builds never show it: there is no ORB connect in the browser, and the
 * webserver reports its own connection state elsewhere.
 */
export function useServerConnecting(): boolean {
  const [connecting, setConnecting] = useState(isTauri());

  useEffect(() => {
    if (!isTauri()) return;
    let cancelled = false;
    const offs: (() => void)[] = [];
    void (async () => {
      try {
        const { listen } = await import("@tauri-apps/api/event");
        const { invoke } = await import("@tauri-apps/api/core");
        const hide = () => setConnecting(false);
        // `server-connected` is an announcement, not an order: re-query the
        // backend before closing. The backend only emits once every startup gate
        // is clear, but an emit site that announces just *before* opening a gate
        // of its own (on_tunnel_ready starting the version check, historically)
        // would otherwise close this dialog for good — it never re-queries, so a
        // single early emit silently lost the whole startup wait. Ignoring a
        // premature announcement costs nothing: every gate-clearing site emits,
        // so whichever clears the last gate announces again.
        const confirmThenHide = () => {
          void (async () => {
            try {
              if (await invoke<boolean>("get_server_connected")) {
                if (!cancelled) hide();
              }
            } catch (e) {
              console.error("server connection re-check failed:", e);
              if (!cancelled) hide();
            }
          })();
        };
        // Subscribe before querying so a connect that lands between the two
        // can't be missed.
        const offListens = await Promise.all([
          listen("server-connected", confirmThenHide),
          listen("version-update-available", hide),
          listen("version-update-restart", hide),
        ]);
        if (cancelled) {
          offListens.forEach((off) => off());
          return;
        }
        offs.push(...offListens);
        // Already connected (the common case — the connect beat the mount):
        // close immediately. Still connecting: stay open until the event.
        if (await invoke<boolean>("get_server_connected")) {
          if (!cancelled) setConnecting(false);
        }
      } catch (e) {
        // Never leave a modal busy dialog wedged over the UI because the probe
        // failed — the connection state is then simply unknown.
        console.error("server connection probe failed:", e);
        if (!cancelled) setConnecting(false);
      }
    })();
    return () => {
      cancelled = true;
      offs.forEach((off) => off());
    };
  }, []);

  return connecting;
}
